import { logger } from "@/lib/logger";

/**
 * Structure of a typical API error response
 */
export interface ApiErrorResponse {
  error: string;
  details?: string;
  code?: string;
  status?: number;
}

/**
 * Extends the standard Error object to include additional properties for API errors
 */
export class ApiError extends Error {
  status?: number;
  code?: string;
  details?: string;

  constructor(
    message: string,
    options?: {
      status?: number;
      code?: string;
      details?: string;
    }
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options?.status;
    this.code = options?.code;
    this.details = options?.details;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Returns true when the error is an ApiError carrying an HTTP 404 status
 */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * Parses an unknown error into an ApiError instance
 */
export function parseApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof Error) {
    try {
      const errorDetails = JSON.parse(error.message) as ApiErrorResponse;
      return new ApiError(errorDetails.error || "Unknown error", {
        details: errorDetails.details,
        status: errorDetails.status,
        code: errorDetails.code,
      });
    } catch {
      return new ApiError(error.message);
    }
  }
  return new ApiError("Unknown error occurred");
}

/**
 * Logs an error with context information
 */
export function logError(error: unknown, context: string): void {
  const apiError = parseApiError(error);
  logger.error(`Error in ${context}:`, apiError, {
    data: {
      details: apiError.details,
      status: apiError.status,
      code: apiError.code,
    },
  });
}

/**
 * Options for showing error toast
 */
export interface ErrorToastOptions {
  title: string;
  defaultMessage: string;
  duration?: number;
}

/**
 * Extracts a user-friendly error message from an error object.
 * Handles ApiError instances and falls back to JSON parsing for backward compatibility.
 *
 * @param error - The error object to extract message from
 * @param defaultMessage - Default message to return if error parsing fails
 * @returns Formatted error message string
 */
export function getErrorMessage(
  error: unknown,
  defaultMessage: string
): string {
  // 1. Check for ApiError instance (new pattern)
  if (error instanceof ApiError) {
    if (error.details) {
      return `${error.message}: ${error.details}`;
    }
    return error.message || defaultMessage;
  }

  // 2. Fallback: try JSON.parse for backward compatibility
  if (error instanceof Error) {
    try {
      const errorDetails = JSON.parse(error.message);
      if (errorDetails.error && errorDetails.details) {
        return `${errorDetails.error}: ${errorDetails.details}`;
      }
      return errorDetails.error || error.message || defaultMessage;
    } catch {
      return error.message || defaultMessage;
    }
  }

  return defaultMessage;
}

/**
 * Shows an error toast notification using the extracted error message.
 * This is a convenience function that combines getErrorMessage with toast notification.
 * Note: This function should only be used in client-side contexts where 'sonner' is available.
 *
 * @param error - The error object to extract message from
 * @param options - Toast options including title, defaultMessage, and optional duration
 */
export async function showErrorToast(
  error: unknown,
  options: ErrorToastOptions
): Promise<void> {
  // Lazy import to avoid bundling issues in server contexts

  const { toast } = await import("sonner");
  const { title, defaultMessage, duration = 5000 } = options;
  const errorMessage = getErrorMessage(error, defaultMessage);
  toast.error(title, {
    description: errorMessage,
    duration,
  });
}
